import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { ArrowsPointingInIcon, ArrowsPointingOutIcon } from '@heroicons/react/24/outline';

import { DrawerNoHeader } from '../../components/drawer/DrawerNoHeader';
import { ImageZoom } from '../../components/imageZoom/ImageZoom';
import { LoaderSection } from '../../components/LoaderSection';
import { InputDateModern } from '../../components/inputDateModern/InputDateModern';
import { ComboBox } from '../../components/comboBox/ComboBox';
import { InputCurrency } from '../../components/inputs/InputCurrency';
import { InputSelectAccountBank } from '../../components/inputSelecList/InputSelectAccountBank';
import { Button } from '../../components/buttons/Button';
import { AvatarWithInitials } from '../../components/avatar/AvatarWithInitials';
import { CounterModern } from '../../components/counterModern';
import { CourseCheckboxSelector } from './CourseCheckboxSelector';
import { ModalCancelPreRegister } from './ModalCancelPreRegister';

import { capitalizarPalabras } from '../../common/upperCaseWord';
import { formatDate, formatDateTime } from '../../common/formatDateText';
import { getSelectedPreRegister, validateCandidate } from '../../redux/actions/preRegistration';
import { optionsAllAccountsBank, optionsAllCourseList, optionsCoordinadors } from '../../redux/actions/options';

const STATUS_WITH_FORM = ['validando', 'validado'];

const VALIDATION_FIELD_LABELS = {
	idCourses: 'Selección de curso(s)',
	paidWeeks: 'Semanas pagadas',
	accountId: 'Cuenta bancaria del comprobante',
	amount: 'Monto del pago (comprobante)',
	subscriptionPayment: 'Monto de suscripción del estudiante',
	paymentReceiptDate: 'Fecha del recibo de pago',
	paymentDeadlineDate: 'Fecha de vencimiento de suscripción',
	coordinador: 'Coordinador',
};

const EMPTY_FORM_ERRORS = {
	idCourses: '',
	paidWeeks: '',
	accountId: '',
	amount: '',
	subscriptionPayment: '',
	paymentReceiptDate: '',
	paymentDeadlineDate: '',
	coordinador: '',
};

const parsePositiveAmount = (value) => {
	if (value === null || value === undefined || value === '') return null;
	const n = Number(value);
	return Number.isFinite(n) && n > 0 ? n : null;
};

export const DrawerCandidateValidation = ({
	isOpenDrawer,
	toggleDrawer,
	idPreRegister,
	updateDataTable,
}) => {
	const dispatch = useDispatch();
	const { preRegisterSelected, loadingSelectedPreRegister: loading } = useSelector((state) => state.preRegistration);
	const { accountsBank, coordinadors, coursesList } = useSelector((state) => state.options);
	const { user } = useSelector((state) => state.user);

	const [loadingSubmit, setLoadingSubmit] = useState(false);
	const [openRejectModal, setOpenRejectModal] = useState(false);
	const [findCoordinator, setFindCoordinator] = useState('');
	const [selectedCourseIds, setSelectedCourseIds] = useState([]);
	const [formData, setFormData] = useState({
		paidWeeks: 0,
		accountId: '',
		amount: '',
		subscriptionPayment: '',
		paymentReceiptDate: null,
		paymentDeadlineDate: null,
		coordinador: '',
	});
	const [formErrors, setFormErrors] = useState({ ...EMPTY_FORM_ERRORS });
	const [showValidationSummary, setShowValidationSummary] = useState(false);
	const validationSummaryRef = useRef(null);
	const [activeMenuOption, setActiveMenuOption] = useState(null);

	const isAuxPanelOpen = Boolean(activeMenuOption);
	const receiptImageUrl = preRegisterSelected?.urlName;
	/** Con el panel del comprobante abierto el espacio útil es menor: una sola columna. */
	const detailsGridClass = isAuxPanelOpen
		? 'grid grid-cols-1 gap-4'
		: 'grid grid-cols-1 md:grid-cols-2 gap-4';
	const validationFormGridClass = isAuxPanelOpen
		? 'grid grid-cols-1 gap-x-4'
		: 'grid grid-cols-1 md:grid-cols-2 gap-x-4';

	const filteredCoordinators =
		findCoordinator === ''
			? coordinadors
			: coordinadors.filter((coord) =>
				coord.description
					.toLowerCase()
					.replace(/\s+/g, '')
					.includes(findCoordinator.toLowerCase().replace(/\s+/g, ''))
			);

	useEffect(() => {
		if (!idPreRegister) return;
		dispatch(getSelectedPreRegister(idPreRegister));
		dispatch(optionsCoordinadors());
		dispatch(optionsAllAccountsBank());
		dispatch(optionsAllCourseList());
	}, [dispatch, idPreRegister]);

	useEffect(() => {
		if (!preRegisterSelected || !isOpenDrawer) return;
		const coordinatorOption =
			coordinadors.find((coord) => coord.value === preRegisterSelected?.coordinador?._id) || '';
		setFormData({
			paidWeeks: preRegisterSelected?.paidWeeks || 0,
			accountId: preRegisterSelected?.account?._id || preRegisterSelected?.account || '',
			amount: preRegisterSelected?.amount || '',
			subscriptionPayment: '',
			paymentReceiptDate: preRegisterSelected?.paymentReceiptDate ? new Date(preRegisterSelected.paymentReceiptDate) : null,
			paymentDeadlineDate: preRegisterSelected?.paymentDeadlineDate ? new Date(preRegisterSelected.paymentDeadlineDate) : null,
			coordinador: coordinatorOption,
		});
		setFormErrors({ ...EMPTY_FORM_ERRORS });
		setShowValidationSummary(false);
		setSelectedCourseIds([]);
	}, [preRegisterSelected, coordinadors, isOpenDrawer]);

	const canValidate = STATUS_WITH_FORM.includes(preRegisterSelected?.status);

	useEffect(() => {
		if (formData.paidWeeks > 0) {
			setFormErrors((prev) => ({ ...prev, paidWeeks: '' }));
		}
	}, [formData.paidWeeks]);

	useEffect(() => {
		if (formData.coordinador?.value) {
			setFormErrors((prev) => ({ ...prev, coordinador: '' }));
		}
	}, [formData.coordinador]);
	useEffect(() => {
		if (formData.accountId) {
			setFormErrors((prev) => ({ ...prev, accountId: '' }));
		}
	}, [formData.accountId]);

	useEffect(() => {
		if (formData.amount && Number(formData.amount) > 0) {
			setFormErrors((prev) => ({ ...prev, amount: '' }));
		}
	}, [formData.amount]);

	useEffect(() => {
		if (formData.subscriptionPayment && Number(formData.subscriptionPayment) > 0) {
			setFormErrors((prev) => ({ ...prev, subscriptionPayment: '' }));
		}
	}, [formData.subscriptionPayment]);

	useEffect(() => {
		if (formData.paymentReceiptDate) {
			setFormErrors((prev) => ({ ...prev, paymentReceiptDate: '' }));
		}
	}, [formData.paymentReceiptDate]);

	useEffect(() => {
		if (formData.paymentDeadlineDate) {
			setFormErrors((prev) => ({ ...prev, paymentDeadlineDate: '' }));
		}
	}, [formData.paymentDeadlineDate]);

	const handleCounterChange = (value) => {
		setFormErrors((prev) => ({ ...prev, paidWeeks: '' }));
		setFormData((prev) => ({ ...prev, paidWeeks: value }));
	};

	const validateForm = () => {
		const weeks = Number(formData.paidWeeks);
		const coordinador = formData.coordinador?.value || '';
		const nextErrors = { ...EMPTY_FORM_ERRORS };
		let isValid = true;

		if (!selectedCourseIds.length) {
			nextErrors.idCourses = coursesList?.length
				? 'Debes seleccionar al menos un curso de la lista.'
				: 'No hay cursos con cupo disponibles. Habilita o crea cursos antes de validar al candidato.';
			isValid = false;
		}
		if (!Number.isFinite(weeks) || weeks < 1) {
			nextErrors.paidWeeks = 'Indica al menos 1 semana pagada.';
			isValid = false;
		}
		if (!formData.accountId) {
			nextErrors.accountId = 'Selecciona la cuenta bancaria del comprobante.';
			isValid = false;
		}
		if (!parsePositiveAmount(formData.amount)) {
			nextErrors.amount = 'Ingresa el monto del comprobante (debe ser mayor a $0).';
			isValid = false;
		}
		if (!parsePositiveAmount(formData.subscriptionPayment)) {
			nextErrors.subscriptionPayment = 'Ingresa el monto de suscripción del estudiante (debe ser mayor a $0).';
			isValid = false;
		}
		if (!formData.paymentReceiptDate) {
			nextErrors.paymentReceiptDate = 'Selecciona la fecha del recibo de pago.';
			isValid = false;
		}
		if (!formData.paymentDeadlineDate) {
			nextErrors.paymentDeadlineDate = 'Selecciona la fecha de vencimiento de la suscripción.';
			isValid = false;
		}
		if (!coordinador) {
			nextErrors.coordinador = 'Selecciona el coordinador responsable.';
			isValid = false;
		}

		setFormErrors(nextErrors);

		if (!isValid) {
			setShowValidationSummary(true);
			const missingLabels = Object.entries(nextErrors)
				.filter(([, msg]) => msg)
				.map(([key]) => VALIDATION_FIELD_LABELS[key]);
			toast.error(
				missingLabels.length === 1
					? `Revisa: ${missingLabels[0]}`
					: `Revisa: ${missingLabels.join(' · ')}`,
				{ duration: 6000 },
			);
			requestAnimationFrame(() => {
				validationSummaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
			});
			return false;
		}

		setShowValidationSummary(false);
		return true;
	};

	const handleValidate = () => {
		if (!validateForm()) return;
		setLoadingSubmit(true);
		dispatch(
			validateCandidate({
				idPreregister: preRegisterSelected?._id,
				createdBy: user?._id,
				coordinador: formData.coordinador.value,
				subscriptionPayment: Number(formData.subscriptionPayment),
				amount: Number(formData.amount),
				paymentReceiptDate: formData.paymentReceiptDate,
				paymentDeadlineDate: formData.paymentDeadlineDate,
				paidWeeks: Number(formData.paidWeeks),
				idCourses: selectedCourseIds,
				message: '',
				account: formData.accountId,
			})
		).then((result) => {
			if (result?.status === 201 || result?.status === 200) {
				toast.success(result.message || 'Preregistro validado correctamente');
				if (updateDataTable) updateDataTable();
				toggleDrawer();
			} else {
				toast.error(result?.message || 'No se pudo validar el preregistro');
			}
			setLoadingSubmit(false);
		});
	};

	useEffect(() => {
		if (selectedCourseIds.length) {
			setFormErrors((prev) => ({ ...prev, idCourses: '' }));
		}
	}, [selectedCourseIds]);

	useEffect(() => {
		if (!isOpenDrawer) {
			setOpenRejectModal(false);
			setShowValidationSummary(false);
			setActiveMenuOption(null);
		}
	}, [isOpenDrawer]);

	useEffect(() => {
		setActiveMenuOption(null);
	}, [idPreRegister]);

	const handleSelectMenuOption = (optionId) => {
		setActiveMenuOption((prev) => (prev === optionId ? null : optionId));
	};

	const comprobanteInner = (
		<>
			<div className='flex items-center gap-2 mb-4 pb-3 border-b border-gray-100'>
				<svg className='w-5 h-5 text-indigo-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
					<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' />
				</svg>
				<h4 className='text-lg font-bold text-gray-800'>Comprobante de Pago</h4>
			</div>
			<div className='w-full flex flex-col justify-center items-center py-4 border-dashed border-spacing-4 border-2 border-gray-200 rounded-lg min-h-[280px] md:min-h-[400px]'>
				{loading && (
					<div className='w-full flex justify-center items-center min-h-[280px]'>
						<LoaderSection />
					</div>
				)}
				{!loading && receiptImageUrl && (
					<ImageZoom key={preRegisterSelected?._id || idPreRegister} imageUrl={receiptImageUrl} />
				)}
				{!loading && !receiptImageUrl && (
					<span className='text-gray-600 text-center text-sm px-4'>
						No se ha subido un comprobante de pago
					</span>
				)}
			</div>
		</>
	);

	return (
		<>
			<DrawerNoHeader
				isOpen={isOpenDrawer}
				toggleDrawer={loadingSubmit ? () => { } : toggleDrawer}
				direction='right'
				menuBar={{
					enabled: true,
					visible: !loading,
					activeId: activeMenuOption,
					onSelect: handleSelectMenuOption,
					buttons: [
						{
							id: 'aux-space',
							title: isAuxPanelOpen ? 'Ocultar comprobante de pago' : 'Mostrar comprobante de pago',
							icon: isAuxPanelOpen
								? <ArrowsPointingInIcon className='h-6 w-6' aria-hidden />
								: <ArrowsPointingOutIcon className='h-6 w-6' aria-hidden />,
						},
					],
				}}
				menuExpand={{
					enabled: isOpenDrawer,
					isExpanded: isAuxPanelOpen,
					sizeLG: '75%',
					sizeXL: '70%',
					sizeXXL: '60%',
				}}
				footer={(
					<div className='flex w-full flex-nowrap items-center justify-end gap-3 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'>
						<Button
							type="button"
							variant="secondary"
							onClick={toggleDrawer}
							disabled={loadingSubmit}
						>
							Cerrar
						</Button>
						{canValidate && (
							<Button
								type="button"
								variant="reject"
								onClick={() => setOpenRejectModal(true)}
								disabled={loadingSubmit}
							>
								Rechazar
							</Button>
						)}
						{canValidate && (
							<Button
								type="button"
								variant="primary"
								loading={loadingSubmit}
								loadingLabel="Validando..."
								onClick={handleValidate}
							>
								Validar candidato
							</Button>
						)}
					</div>
				)}
			>
				<div className='w-full h-full flex flex-col overflow-hidden'>
					<div className='w-full bg-white rounded-lg border border-gray-200 flex-shrink-0'>
						<div className='bg-gradient-to-r from-indigo-500 to-purple-600 p-5 md:p-6 rounded-tr-lg'>
							<div className='flex items-center gap-4 md:gap-5'>
								<div className='relative flex-shrink-0'>
									<AvatarWithInitials
										firstName={preRegisterSelected?.firstName || ''}
										lastName={preRegisterSelected?.lastName || ''}
										loading={loading}
										size='md'
									/>
								</div>
								<div className='flex-1 min-w-0'>
									<h3 className='text-white font-bold text-lg md:text-xl lg:text-2xl capitalize tracking-tight'>
										{`${preRegisterSelected?.firstName || ''} ${preRegisterSelected?.secondName || ''} ${preRegisterSelected?.lastName || ''} ${preRegisterSelected?.secondSurname || ''}`.trim() || 'Candidato'}
									</h3>
									<div className='flex flex-wrap gap-2 mt-2'>
										<span className='inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/30 text-white text-xs md:text-sm font-semibold uppercase'>
											{'Candidato'}
										</span>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className='flex-1 overflow-hidden min-h-0 flex flex-col'>
						{loading ? (
							<div className='w-full flex-1 flex items-center justify-center p-4'>
								<LoaderSection />
							</div>
						) : (
							<div className='flex-1 min-h-0 flex flex-col'>
								<div className='flex-shrink-0 px-4 pt-4 md:px-5 md:pt-5'>
								<div className='flex-shrink-0 mb-6'>
									<div className='bg-white border border-gray-200 rounded-lg px-5 py-4 shadow-sm'>
										<div className='flex items-center justify-between'>
											<div className='flex items-center gap-3'>
												<div>
													<h3 className='text-xl font-bold text-gray-900'>
														Detalles del candidato
													</h3>
													<p className='text-xs text-gray-500 font-medium mt-0.5 hidden md:block'>
														Se muestra los detalles del candidato y del pago.
													</p>
												</div>
											</div>
										</div>
									</div>
								</div>
								</div>

								<div className='flex-1 min-h-0 flex gap-4 px-4 pb-4 md:px-5 md:pb-5'>
									{isAuxPanelOpen && (
										<div className='hidden h-full min-h-0 shrink-0 self-stretch xl:flex'>
											<div className='flex h-full min-h-0 w-[25vw] flex-col rounded-lg bg-white'>
												<div className='min-h-0 flex-1 overflow-auto'>
													<div className='bg-white border border-gray-200 rounded-lg p-5'>
														{comprobanteInner}
													</div>
												</div>
											</div>
										</div>
									)}
									<div className='flex-1 min-w-0 min-h-0 overflow-y-auto pr-2'>
								{/* Información del Usuario — mismo patrón que TabPaymentDetails */}
								<div className='bg-white border border-gray-200 rounded-lg p-5 mb-5'>
									<div className='flex items-center gap-2 mb-4 pb-3 border-b border-gray-100'>
										<svg className='w-5 h-5 text-indigo-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
											<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' />
										</svg>
										<h4 className='text-lg font-bold text-gray-800'>Información del Usuario</h4>
									</div>
									<div className={detailsGridClass}>
										<div className='space-y-1'>
											<label className='text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide'>Nombre completo</label>
											<p className='text-[.9rem] font-medium text-gray-900 capitalize'>
												{`${capitalizarPalabras(preRegisterSelected?.firstName || '')} ${capitalizarPalabras(preRegisterSelected?.secondName || '')} ${capitalizarPalabras(preRegisterSelected?.lastName || '')} ${capitalizarPalabras(preRegisterSelected?.secondSurname || '')}`.trim() || '-'}
											</p>
										</div>
										<div className='space-y-1'>
											<label className='text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide'>Correo Electrónico</label>
											<p className='text-[.9rem] font-medium text-gray-900 break-words'>{preRegisterSelected?.email || '-'}</p>
										</div>
										<div className='space-y-1'>
											<label className='text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide'>Teléfono</label>
											<p className='text-[.9rem] font-medium text-gray-900'>{preRegisterSelected?.phone || '-'}</p>
										</div>
										<div className='space-y-1'>
											<label className='text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide'>Estatus del preregistro</label>
											<div className='flex items-center gap-2 flex-wrap'>
												{preRegisterSelected?.status === 'completado' && (
													<span className='inline-flex items-center gap-1.5 bg-emerald-100 text-emerald-600 px-3 py-1.5 rounded-lg font-semibold text-sm uppercase'>
														<svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
															<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
														</svg>
														Completado
													</span>
												)}
												{preRegisterSelected?.status === 'prospecto' && (
													<span className='inline-flex items-center gap-1.5 bg-orange-100 text-orange-700 px-3 py-1.5 rounded-lg font-semibold text-sm uppercase'>
														<svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
															<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' />
														</svg>
														Prospecto
													</span>
												)}
												{(preRegisterSelected?.status === 'validando' || preRegisterSelected?.status === 'validado') && (
													<span className='inline-flex items-center gap-1.5 bg-rose-100 text-rose-600 px-3 py-1.5 rounded-lg font-semibold text-sm uppercase'>
														<svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
															<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' />
														</svg>
														{preRegisterSelected?.status}
													</span>
												)}
												{!['completado', 'prospecto', 'validando', 'validado'].includes(preRegisterSelected?.status) && (
													<span className='text-[.9rem] font-medium text-gray-900 capitalize'>{preRegisterSelected?.status || '-'}</span>
												)}
											</div>
										</div>
										<div className='space-y-1'>
											<label className='text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide'>Fecha de registro</label>
											<p className='text-[.9rem] font-medium text-gray-900 uppercase'>{preRegisterSelected?.createdAt ? formatDateTime(preRegisterSelected.createdAt) : '-'}</p>
										</div>
										<div className='space-y-1'>
											<label className='text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide'>Fecha de nacimiento</label>
											<p className='text-[.9rem] font-medium text-gray-900 uppercase'>{preRegisterSelected?.dateBirth ? formatDate(preRegisterSelected.dateBirth) : '-'}</p>
										</div>
										<div className='space-y-1'>
											<label className='text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide'>Ubicación</label>
											<p className='text-[.9rem] font-medium text-gray-900 capitalize'>{preRegisterSelected?.location || '-'}</p>
										</div>
										<div className='space-y-1'>
											<label className='text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide'>Nivel educativo</label>
											<p className='text-[.9rem] font-medium text-gray-900 capitalize'>{preRegisterSelected?.education || '-'}</p>
										</div>
										<div className='space-y-1'>
											<label className='text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide'>Términos y condiciones</label>

											<div className="flex items-center gap-2">
												{preRegisterSelected.usageRights ? (
													<>
														<svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
														</svg>
														<span className="text-[.9rem] font-semibold text-emerald-700">Firmado</span>
													</>
												) : (
													<>
														<svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
														</svg>
														<span className="text-[.9rem] font-semibold text-red-700">No Firmado</span>
													</>
												)}
											</div>
										</div>
										<div className='space-y-1'>
											<label className='text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide'>RFC</label>
											<p className='text-[.9rem] font-medium text-gray-900 uppercase'>{preRegisterSelected?.rfc || '-'}</p>
										</div>
									</div>
								</div>

								{/* Comprobante: en xl+ se muestra en panel auxiliar si está abierto; en móvil/tablet siempre aquí */}
								<div
									className={`bg-white border border-gray-200 rounded-lg p-5 mb-5 ${
										isAuxPanelOpen ? 'block xl:hidden' : ''
									}`}
								>
									{comprobanteInner}
								</div>

								{canValidate && (
									<div className='bg-white border border-gray-200 rounded-lg p-5 mb-5'>
										<div className='flex items-center gap-2 mb-4 pb-3 border-b border-gray-100'>
											<svg className='w-5 h-5 text-indigo-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
												<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0121 12c0 5.523-4.477 10-10 10S1 17.523 1 12 5.477 2 11 2c1.821 0 3.53.456 5.03 1.26' />
											</svg>
											<h4 className='text-lg font-bold text-gray-800'>Validación del candidato</h4>
										</div>
										<div className='mb-5'>
											<label className='block text-[.7rem] font-semibold text-gray-500 uppercase tracking-wide ml-1 mb-3'>
												Selección de curso(s)<span className='text-red-500 ml-0.5'>*</span>
											</label>
											<CourseCheckboxSelector
												courses={coursesList}
												selectedCourseIds={selectedCourseIds}
												setSelectedCourseIds={setSelectedCourseIds}
												error={formErrors.idCourses}
											/>
										</div>
										<div className={validationFormGridClass}>
											<div className='my-4 md:my-6'>
												<ComboBox
													inputId='candidate-coordinator'
													label='Coordinador'
													required
													error={formErrors.coordinador}
													filterData={filteredCoordinators}
													query={findCoordinator}
													setQuery={setFindCoordinator}
													selected={formData}
													setSelected={setFormData}
													placeholder='Selecciona coordinador'
													property='coordinador'
												/>
											</div>
											<div className='my-4 md:my-6'>
												<InputSelectAccountBank
													name='accountId'
													label='Cuenta bancaria del comprobante'
													placeholder='Selecciona la cuenta bancaria'
													value={formData.accountId}
													onChange={(e) => {
														setFormData((prev) => ({ ...prev, accountId: e.target.value }));
														setFormErrors((prev) => ({ ...prev, accountId: '' }));
													}}
													options={accountsBank}
													required
													error={formErrors.accountId}
												/>
											</div>
											<div className='my-4 md:my-6'>
												<InputDateModern
													id='paymentReceiptDate'
													label='Fecha del recibo de pago'
													value={formData.paymentReceiptDate}
													onChange={(value) => {
														setFormData((prev) => ({ ...prev, paymentReceiptDate: value }));
														setFormErrors((prev) => ({ ...prev, paymentReceiptDate: '' }));
													}}
													required
													error={formErrors.paymentReceiptDate}
													placeholder='Fecha del recibo de pago'
													popperPlacement='top-start'
												/>
											</div>
											<div className='my-4 md:my-6'>
												<InputDateModern
													id='paymentDeadlineDate'
													label='Fecha de vencimiento de suscripción'
													value={formData.paymentDeadlineDate}
													onChange={(value) => {
														setFormData((prev) => ({ ...prev, paymentDeadlineDate: value }));
														setFormErrors((prev) => ({ ...prev, paymentDeadlineDate: '' }));
													}}
													required
													error={formErrors.paymentDeadlineDate}
													minDate={new Date()}
													placeholder='Fecha de vencimiento de suscripción'
													popperPlacement='top-start'
												/>
											</div>
											<div className='my-4 md:my-6'>
												<InputCurrency
													name='amount'
													label='Monto del pago (comprobante)'
													placeholder='$ 0.00'
													formData={formData}
													setFormData={setFormData}
													required
													error={formErrors.amount}
												/>
											</div>
											<div className='my-4 md:my-6'>
												<InputCurrency
													name='subscriptionPayment'
													label='Monto de suscripción del estudiante'
													placeholder='$ 0.00'
													formData={formData}
													setFormData={setFormData}
													required
													error={formErrors.subscriptionPayment}
												/>
											</div>
											<div className='my-4 md:my-6'>
												<CounterModern
													id='candidate-paid-weeks'
													label='Semanas pagadas'
													required
													value={formData.paidWeeks}
													onChange={handleCounterChange}
													min={0}
													error={formErrors.paidWeeks}
												/>
											</div>
										</div>
										{showValidationSummary && Object.values(formErrors).some(Boolean) && (
											<div
												ref={validationSummaryRef}
												className='mt-4 rounded-lg border border-red-200 bg-red-50 p-4'
												role='alert'
											>
												<p className='text-sm font-semibold text-red-800 mb-2'>
													Corrige los siguientes campos para validar al candidato:
												</p>
												<ul className='list-disc space-y-1.5 pl-5 text-sm text-red-700'>
													{Object.entries(formErrors)
														.filter(([, msg]) => msg)
														.map(([key, msg]) => (
															<li key={key}>
																<span className='font-semibold'>{VALIDATION_FIELD_LABELS[key]}:</span>{' '}
																{msg}
															</li>
														))}
												</ul>
											</div>
										)}
										<div className='mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg'>
											<p className='text-sm text-blue-800 text-center'>
												<svg className='w-4 h-4 inline-block mr-1' fill='currentColor' viewBox='0 0 20 20'>
													<path fillRule='evenodd' d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z' clipRule='evenodd' />
												</svg>
												Los campos marcados con * son obligatorios para registrar al estudiante en la plataforma
											</p>
										</div>
									</div>
								)}
									</div>
								</div>
							</div>
						)}
					</div>
				</div>
			</DrawerNoHeader>
			<ModalCancelPreRegister
				open={openRejectModal}
				setOpen={setOpenRejectModal}
				preRegisterId={preRegisterSelected?._id || idPreRegister}
				onSuccess={(result) => {
					toast.success(result?.message || 'Se ha notificado al candidato sobre la revisión de su preinscripción.');
					if (updateDataTable) updateDataTable();
					toggleDrawer();
				}}
			/>
		</>
	);
};
